import { cn } from "@/lib/utils";
import { type VariantProps, cva } from "class-variance-authority";
import { cloneElement, isValidElement, type ButtonHTMLAttributes, type ReactElement } from "react";
import { Spinner } from "./spinner";

/**
 * Base button. Exported as `buttonVariants` + `Button` because the Kokonut UI
 * components in `@/components/kokonutui` compose the variant styles directly.
 * `asChild` mirrors the shadcn API so links can wear button styling.
 *
 * `loading` renders a spinner and disables the button, so async actions never have
 * to swap their label text ("Saving…") — the label stays put and the state is
 * communicated by the indicator + `aria-busy`.
 */
export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:brightness-110 shadow-sm",
        secondary: "bg-secondary text-secondary-foreground hover:brightness-110",
        outline: "border border-border bg-transparent hover:bg-muted",
        ghost: "bg-transparent hover:bg-muted",
        destructive: "bg-danger text-danger-foreground hover:brightness-110",
        success: "bg-success text-success-foreground hover:brightness-110",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        sm: "h-8 px-3 text-xs",
        default: "h-10 px-4",
        lg: "h-12 px-6 text-base",
        icon: "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

export const Button = ({
  className,
  variant,
  size,
  type = "button",
  asChild = false,
  loading = false,
  disabled,
  children,
  ...props
}: ButtonProps) => {
  const classes = cn(buttonVariants({ variant, size }), className);

  if (asChild && isValidElement(children)) {
    const child = children as ReactElement<{ className?: string }>;
    return cloneElement(child, { className: cn(classes, child.props.className) });
  }

  return (
    <button
      type={type}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <Spinner /> : null}
      {children}
    </button>
  );
};

export default Button;
